import { type Placement } from "@floating-ui/react";
import rocketIcon from "@tabler/icons/outline/rocket.svg";
import arrowsVerticalIcon from "@tabler/icons/outline/arrows-vertical.svg";
import atIcon from "@tabler/icons/outline/at.svg";
import banIcon from "@tabler/icons/outline/ban.svg";
import bellOffIcon from "@tabler/icons/outline/bell-off.svg";
import bellIcon from "@tabler/icons/outline/bell.svg";
import clipboardCopyIcon from "@tabler/icons/outline/clipboard-copy.svg";
import dotsIcon from "@tabler/icons/outline/dots.svg";
import editIcon from "@tabler/icons/outline/edit.svg";
import flagIcon from "@tabler/icons/outline/flag.svg";
import helpIcon from "@tabler/icons/outline/help.svg";
import pencilIcon from "@tabler/icons/outline/pencil.svg";
import pinIcon from "@tabler/icons/outline/pin.svg";
import pinnedOffIcon from "@tabler/icons/outline/pinned-off.svg";
import shareIcon from "@tabler/icons/outline/share.svg";
import thumbDownIcon from "@tabler/icons/outline/thumb-down.svg";
import trashIcon from "@tabler/icons/outline/trash.svg";
import volume3Icon from "@tabler/icons/outline/volume-3.svg";
import volumeIcon from "@tabler/icons/outline/volume.svg";
import React, { useEffect, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import {
  useAccount,
  useBlockAccount,
  useUnblockAccount,
  useUnmuteAccount,
} from "@/api/accounts";
import {
  useBoostEligibility,
  useBoosts,
  LIVE_BOOST_STATES,
} from "@/api/boosts";
import { queryClient } from "@/api/core";
import { useBlockGroupMember } from "@/api/group-blocks";
import { useGroupRelationship } from "@/api/group-relationships";
import {
  useDeleteGroupStatus,
  useGroup,
  useMuteGroup,
  usePinStatusToGroup,
  useUnmuteGroup,
  useUnpinStatusFromGroup,
} from "@/api/groups";
import { useSuppressAccount, useSuppressGroup } from "@/api/recommendations";
import { useAccountRelationship } from "@/api/relationships";
import {
  useDeleteStatus,
  useMuteStatus,
  usePinStatus,
  useUnmuteStatus,
  useUnpinStatus,
} from "@/api/statuses";
import { StatusQueryKeys } from "@/api/statuses/query-keys";
import { useAnalytics } from "@/contexts/analytics-context";
import { useAuth } from "@/contexts/auth/useAuth";
import { useComposer } from "@/contexts/composer-context";
import { useModalContext } from "@/contexts/modal-context";
import { ReportableEntities } from "@/contexts/reports-context";
import { useTimelineContext } from "@/contexts/timeline-context";
import { BoostEvents } from "@/features/boosts/telemetry";
import { isBoostableStatus } from "@/features/boosts/utils";
import { useFeatures } from "@/hooks";
import {
  MEDIA_ATTACHMENT_PARAM,
  MEDIA_STATUS_PARAM,
  useClearMediaParams,
} from "@/hooks/useMediaDeepLink";
import { PremiumFeatures, type Group, type Status } from "@/schemas";
import { GroupRoles } from "@/schemas/group-member";
import toast from "@/toast";
import copy from "@/utils/copy";
import { unescapeHTML } from "@/utils/html";

import DropdownMenu, { type Menu } from "../dropdown-menu";

import StatusActionGroup, {
  type StatusActionLayout,
} from "./status-action-group";

const messages = defineMessages({
  block: { id: "account.block", defaultMessage: "Block @{name}" },
  blockAndReport: {
    id: "confirmations.block.block_and_report",
    defaultMessage: "Block & Report",
  },
  blockConfirm: { id: "confirmations.block.confirm", defaultMessage: "Block" },
  copied: { id: "copy.success", defaultMessage: "Copied to clipboard!" },
  copy: { id: "status.copy", defaultMessage: "Copy link to Truth" },
  delete: { id: "status.delete", defaultMessage: "Delete" },
  deleteConfirm: {
    id: "confirmations.delete.confirm",
    defaultMessage: "Delete",
  },
  deleteFromGroupMessage: {
    id: "confirmations.delete_from_group.message",
    defaultMessage: "Are you sure you want to delete @{name}'s Truth?",
  },
  deleteHeading: {
    id: "confirmations.delete.heading",
    defaultMessage: "Delete Truth",
  },
  deleteMessage: {
    id: "confirmations.delete.message",
    defaultMessage: "Are you sure you want to delete this Truth?",
  },
  edit: { id: "status.edit", defaultMessage: "Edit Truth" },
  embed: { id: "status.embed", defaultMessage: "Embed Truth" },
  boost: { id: "status.boost", defaultMessage: "Boost Truth" },
  boostMediaOnlyPreview: {
    id: "boosts.preview.media_only",
    defaultMessage:
      "{count, plural, one {Media Truth} other {Media Truth (# attachments)}}",
  },
  groupBlockConfirm: {
    id: "confirmations.block_from_group.confirm",
    defaultMessage: "Ban",
  },
  groupBlockFromGroupHeading: {
    id: "confirmations.block_from_group.heading",
    defaultMessage: "Ban From Group",
  },
  groupBlockFromGroupMessage: {
    id: "confirmations.block_from_group.message",
    defaultMessage: "Are you sure you want to ban @{name} from the group?",
  },
  groupModDelete: {
    id: "status.group_mod_delete",
    defaultMessage: "Delete Truth from group",
  },
  mention: { id: "status.mention", defaultMessage: "Mention @{name}" },
  more: { id: "status.more", defaultMessage: "More" },
  mute: { id: "account.mute", defaultMessage: "Mute @{name}" },
  muteConfirm: {
    id: "confirmations.mute_group.confirm",
    defaultMessage: "Mute",
  },
  muteConversation: {
    id: "status.mute_conversation",
    defaultMessage: "Mute conversation",
  },
  muteGroup: { id: "group.mute.long_label", defaultMessage: "Mute Group" },
  muteHeading: {
    id: "confirmations.mute_group.heading",
    defaultMessage: "Mute Group",
  },
  muteMessage: {
    id: "confirmations.mute_group.message",
    defaultMessage: "You are about to mute the group. Do you want to continue?",
  },
  open: { id: "status.open", defaultMessage: "Show Truth Details" },
  pin: { id: "status.pin", defaultMessage: "Pin on profile" },
  pinToGroup: { id: "status.pin_to_group", defaultMessage: "Pin to Group" },
  redraft: { id: "status.redraft", defaultMessage: "Delete & re-draft" },
  redraftConfirm: {
    id: "confirmations.redraft.confirm",
    defaultMessage: "Delete & Re-Draft",
  },
  redraftHeading: {
    id: "confirmations.redraft.heading",
    defaultMessage: "Delete & Re-Draft",
  },
  redraftMessage: {
    id: "confirmations.redraft.message",
    defaultMessage:
      "Are you sure you want to delete this Truth and re-draft it? Favorites and ReTruths will be lost, and replies to the original Truth will be orphaned.",
  },
  report: { id: "status.report", defaultMessage: "Report @{name}" },
  suppressAccount: {
    id: "status.suppress.account",
    defaultMessage: "Not interested in this profile",
  },
  suppressGroup: {
    id: "status.suppress.group",
    defaultMessage: "Not interested in this group",
  },
  unblock: { id: "account.unblock", defaultMessage: "Unblock @{name}" },
  unmute: { id: "account.unmute", defaultMessage: "Unmute @{name}" },
  unmuteConversation: {
    id: "status.unmute_conversation",
    defaultMessage: "Unmute conversation",
  },
  unmuteGroup: {
    id: "group.unmute.long_label",
    defaultMessage: "Unmute Group",
  },
  unpin: { id: "status.unpin", defaultMessage: "Unpin from profile" },
  unpinFromGroup: {
    id: "status.unpin_to_group",
    defaultMessage: "Unpin from Group",
  },
});

interface StatusMenuProps {
  isExpandable?: boolean;
  isRecommended?: boolean;
  layout: StatusActionLayout;
  placement?: Placement;
  status: Status;
}

export default function StatusMenu(props: StatusMenuProps) {
  const {
    isExpandable = true,
    isRecommended = false,
    layout,
    placement,
    status,
  } = props;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const { currentAccount } = useAuth();
  const { openModal, closeModal } = useModalContext();
  const { pathname } = useLocation();
  const { timelineId } = useTimelineContext();
  const { track } = useAnalytics();
  const features = useFeatures();

  const [hasMenuOpened, setHasMenuOpened] = useState(false);

  const { updateComposer } = useComposer(status.id);

  const { group } = useGroup(status.group?.id, { refetch: false });
  const { groupRelationship } = useGroupRelationship(status.group?.id);
  const { account } = useAccount(status.account.id, { withRelationship: true });
  const { relationship } = useAccountRelationship(status.account.id, {
    enabled: true,
  });

  const blockAccount = useBlockAccount();
  const blockGroupMember = useBlockGroupMember(group!, status.account);
  const deleteGroupStatus = useDeleteGroupStatus();
  const deleteStatus = useDeleteStatus();
  const muteGroup = useMuteGroup();
  const muteStatus = useMuteStatus();
  const pinStatus = usePinStatus();
  const pinStatusToGroup = usePinStatusToGroup(group as Group, status);
  const suppressAccount = useSuppressAccount();
  const suppressGroup = useSuppressGroup();
  const unblockAccount = useUnblockAccount();
  const unmuteAccount = useUnmuteAccount();
  const unmuteGroup = useUnmuteGroup();
  const unmuteStatus = useUnmuteStatus();
  const unpinStatus = useUnpinStatus();
  const unpinStatusFromGroup = useUnpinStatusFromGroup(group as Group, status);

  const clearMediaParams = useClearMediaParams();

  const isGroupStatus = typeof status.group === "object" && !!status.group;
  const isMutingGroup = !!group?.relationship?.muting;
  const isMutingAccount = !!account?.relationship?.muting;
  const isPublicStatus = ["public", "unlisted", "group"].includes(
    status.visibility,
  );

  const isOwnAccount = status.account.id === currentAccount?.id;
  const boostableShape = isBoostableStatus(status);
  const canOfferBoost =
    features.boosts && isOwnAccount && boostableShape.boostable;
  const { data: boostQuote, isLoading: boostQuoteLoading } =
    useBoostEligibility(canOfferBoost ? status.id : null, {
      enabled: canOfferBoost && hasMenuOpened,
    });
  const isBoostEligible = !!boostQuote?.eligible;

  // TMT-193: the user's own boost list is the only boost signal that covers
  // every surface. Feed statuses come from sevro, whose `editable` is
  // boost-unaware and which never sets boost_id on the owner's organic post;
  // profile/detail come from social-v1, whose `editable` is boost-aware.
  // The query is shared app-wide (one cache key), so this costs one fetch.
  const boostsQueryEnabled = features.boosts && isOwnAccount;
  const {
    entities: myBoosts,
    isStale: myBoostsStale,
    refetch: refetchMyBoosts,
  } = useBoosts({ enabled: boostsQueryEnabled });
  const hasLiveBoost = myBoosts.some(
    (boost) =>
      LIVE_BOOST_STATES.has(boost.state) && boost.status?.id === status.id,
  );

  // Opening the menu is the last moment before the user can click Edit, so
  // refresh a stale boost list here to narrow the window where the gate below
  // decides from cached data.
  useEffect(() => {
    if (hasMenuOpened && boostsQueryEnabled && myBoostsStale) {
      refetchMyBoosts();
    }
  }, [hasMenuOpened, boostsQueryEnabled, myBoostsStale, refetchMyBoosts]);

  function trackEvent(name: string) {
    const properties: { feed_type?: string; video_type?: string } = {};

    if (pathname === "/") {
      properties.feed_type = timelineId;
    }

    if (status.tv) {
      properties.video_type =
        new Date(status.tv.end_time) < new Date() ? "Replay" : "Livestream";
    }

    track(name, properties);
  }

  function handleWhyRecommended() {
    openModal("RECOMMENDED_EXPLANATION", { group: !!status.group });
  }

  function handleNotInterested() {
    if (status.group) {
      suppressGroup.mutate({
        groupId: status.group.id,
        statusId: status.id,
      });
    } else {
      suppressAccount.mutate({
        accountId: status.account.id,
        statusId: status.id,
      });
    }

    trackEvent("TappedNotInterested");
  }

  function handleOpen() {
    navigate(`/@${status.account.acct}/posts/${status.id}`);
  }

  function handleCopy() {
    copy(status.url, () => {
      toast.success(messages.copied);
      trackEvent("SharedTruth");
    });
  }

  function handleEmbed() {
    openModal("EMBED", {
      url: status.url,
      onError: (error: any) => toast.showAlertForError(error),
    });
  }

  function handleGroupPinClick() {
    if (status.pinned) {
      unpinStatusFromGroup.mutate({ id: status.id });
    } else {
      pinStatusToGroup.mutate({ id: status.id });
    }
  }

  function handleConversationMuteClick() {
    if (status.muted) {
      unmuteStatus.mutate({ id: status.id });
    } else {
      muteStatus.mutate({ id: status.id });
    }
  }

  function handlePinClick() {
    if (status.pinned) {
      unpinStatus.mutate({ id: status.id });
    } else {
      pinStatus.mutate({ id: status.id });
    }
  }

  function handleDeleteStatus(withRedraft = false) {
    const deleteModal = currentAccount?.settings?.deleteModal;
    if (!deleteModal) {
      deleteStatus.mutate({ id: status.id, withRedraft });
    } else {
      openModal("CONFIRM", {
        heading: intl.formatMessage(
          withRedraft ? messages.redraftHeading : messages.deleteHeading,
        ),
        message: intl.formatMessage(
          withRedraft ? messages.redraftMessage : messages.deleteMessage,
        ),
        confirm: intl.formatMessage(
          withRedraft ? messages.redraftConfirm : messages.deleteConfirm,
        ),
        onConfirm: () => {
          deleteStatus.mutate({ id: status.id, withRedraft });
        },
      });
    }

    // if this action comes from media modal, clear params and close modal
    if (
      searchParams.get(MEDIA_STATUS_PARAM) ||
      searchParams.get(MEDIA_ATTACHMENT_PARAM)
    ) {
      clearMediaParams();
      closeModal();
    }
  }

  function handleDeleteClick() {
    handleDeleteStatus();
  }

  function handleRedraftClick() {
    handleDeleteStatus(true);
  }

  function handleMentionClick() {
    openModal("COMPOSE", { composeId: status.id });
    updateComposer({ text: `@${status.account.username} ` });
  }

  function handleMuteClick() {
    if (isMutingAccount) {
      unmuteAccount.mutate({ account });
    } else {
      openModal("MUTE", { accountId: status.account.id });
    }
  }

  function handleMuteGroupClick() {
    openModal("CONFIRM", {
      heading: intl.formatMessage(messages.muteHeading),
      message: intl.formatMessage(messages.muteMessage),
      confirm: intl.formatMessage(messages.muteConfirm),
      confirmationTheme: "default",
      onConfirm: () => muteGroup.mutate({ id: group!.relationship!.id }),
    });
  }

  function handleUnmuteGroupClick() {
    unmuteGroup.mutate({ id: group!.relationship!.id });
  }

  function handleBlockClick() {
    const account = status.account;

    openModal("CONFIRM", {
      heading: (
        <FormattedMessage
          id="confirmations.block.heading"
          defaultMessage="Block @{name}"
          values={{ name: account.acct }}
        />
      ),
      message: (
        <FormattedMessage
          id="confirmations.block.message"
          defaultMessage="Are you sure you want to block {name}?"
          values={{
            name: <strong className="wrap-break-word">@{account.acct}</strong>,
          }}
        />
      ),
      confirm: intl.formatMessage(messages.blockConfirm),
      onConfirm: () => blockAccount.mutate({ account }),
      secondary: intl.formatMessage(messages.blockAndReport),
      onSecondary: () => {
        blockAccount.mutate({ account });
        openModal("REPORT", {
          entityType: ReportableEntities.STATUS,
          account,
          status,
        });
      },
    });
  }

  function handleUnblockClick(event: React.MouseEvent) {
    event.preventDefault();

    unblockAccount.mutate({ account: status.account });
  }

  function handleReport() {
    const account = status.account;

    openModal("REPORT", {
      entityType: ReportableEntities.STATUS,
      account,
      status,
    });
  }

  function handleDeleteFromGroup() {
    const account = status.account;

    openModal("CONFIRM", {
      heading: intl.formatMessage(messages.deleteHeading),
      message: intl.formatMessage(messages.deleteFromGroupMessage, {
        name: <strong className="wrap-break-word">{account.username}</strong>,
      }),
      confirm: intl.formatMessage(messages.deleteConfirm),
      onConfirm: () =>
        deleteGroupStatus.mutate({ groupId: group!.id, id: status.id }),
    });
  }

  function handleBlockFromGroup() {
    openModal("CONFIRM", {
      heading: intl.formatMessage(messages.groupBlockFromGroupHeading),
      message: intl.formatMessage(messages.groupBlockFromGroupMessage, {
        name: (status.account as any).username,
      }),
      confirm: intl.formatMessage(messages.groupBlockConfirm),
      onConfirm: () => {
        blockGroupMember.mutate();
      },
    });
  }

  function openEditModal() {
    openModal("COMPOSE", { composeId: status.id, status: status });
  }

  function handleEdit() {
    if (!currentAccount?.features?.edits_enabled) {
      openModal("UNLOCK_FEATURE", { feature: PremiumFeatures.EDITS });
      return;
    }
    if (currentAccount?.source.accepted_status_edit_prompt) {
      openEditModal();
    } else {
      openModal("EDIT_STATUSES_ONBOARDING", { onConfirm: openEditModal });
    }
  }

  function handleBoostClick() {
    if (!boostQuote?.eligible) return;
    track(BoostEvents.BoostButtonClicked, { source: "status_menu" });
    const mediaCount = status.media_attachments?.length ?? 0;
    const previewText =
      unescapeHTML(status.content) ||
      (mediaCount > 0
        ? intl.formatMessage(messages.boostMediaOnlyPreview, {
            count: mediaCount,
          })
        : "");
    openModal("BOOST_CONFIRM", {
      mode: "existing",
      statusId: status.id,
      previewAccount: status.account,
      previewText,
      quote: boostQuote,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: StatusQueryKeys.detail(status.id),
        });
      },
    });
  }

  function buildMenuItems() {
    const mutingConversation = status.muted;
    const username = status.account.username;

    const items: Menu = [];

    if (isRecommended) {
      items.push({
        text: intl.formatMessage(
          status.group ? messages.suppressGroup : messages.suppressAccount,
        ),
        action: handleNotInterested,
        icon: thumbDownIcon,
      });

      items.push({
        text: "Why am I seeing this?",
        action: handleWhyRecommended,
        icon: helpIcon,
      });

      items.push(null);
    }

    if (isExpandable) {
      items.push({
        text: intl.formatMessage(messages.open),
        action: handleOpen,
        icon: arrowsVerticalIcon,
      });
    }

    if (isPublicStatus) {
      items.push({
        text: intl.formatMessage(messages.copy),
        action: handleCopy,
        icon: clipboardCopyIcon,
      });

      if (!status?.group?.locked) {
        items.push({
          text: intl.formatMessage(messages.embed),
          action: handleEmbed,
          icon: shareIcon,
        });
      }
    }

    // TMT-193: hide Edit on boosted Truths. The server `editable` field is NOT
    // used here: it also goes false for non-premium accounts (which must keep
    // Edit as the UNLOCK_FEATURE upsell entry point) and past the edit window.
    // Hiding (vs disabling) is an explicit ticket requirement.
    //
    // This gate fails open: while the boost list is loading, errored, or stale
    // it shows no live boost and Edit stays visible. That is deliberate — the
    // backstop is social-v1's StatusUpdateValidator (TMT-193 companion), which
    // rejects the edit server-side for every live boost state.
    const editHiddenForBoost = !!status.boost_id || hasLiveBoost;
    if (
      currentAccount?.features?.edits_visible &&
      isOwnAccount &&
      !status.poll &&
      !editHiddenForBoost
    ) {
      items.push({
        text: intl.formatMessage(messages.edit),
        action: handleEdit,
        icon: pencilIcon,
      });
    }

    if (!currentAccount) {
      return items;
    }

    if (isGroupStatus) {
      const canPinToGroup =
        groupRelationship?.role === GroupRoles.OWNER ||
        groupRelationship?.role === GroupRoles.ADMIN;

      if (canPinToGroup) {
        items.push({
          text: intl.formatMessage(
            status.pinned ? messages.unpinFromGroup : messages.pinToGroup,
          ),
          action: handleGroupPinClick,
          icon: status.pinned ? pinnedOffIcon : pinIcon,
        });
      }
    }

    items.push(null);

    items.push({
      text: intl.formatMessage(
        mutingConversation
          ? messages.unmuteConversation
          : messages.muteConversation,
      ),
      action: handleConversationMuteClick,
      icon: mutingConversation ? bellIcon : bellOffIcon,
    });

    items.push(null);

    if (isOwnAccount) {
      if (canOfferBoost) {
        items.push({
          text: intl.formatMessage(messages.boost),
          action: handleBoostClick,
          icon: rocketIcon,
          disabled: boostQuoteLoading || !isBoostEligible,
          testId: "status-menu-boost",
        });
      }

      if (isPublicStatus && !isGroupStatus) {
        items.push({
          text: intl.formatMessage(
            status.pinned ? messages.unpin : messages.pin,
          ),
          action: handlePinClick,
          icon: status.pinned ? pinnedOffIcon : pinIcon,
        });
      }

      items.push({
        text: intl.formatMessage(messages.delete),
        action: handleDeleteClick,
        icon: trashIcon,
        destructive: true,
      });

      items.push({
        text: intl.formatMessage(messages.redraft),
        action: handleRedraftClick,
        icon: editIcon,
        destructive: true,
      });
    } else {
      items.push({
        text: intl.formatMessage(messages.mention, { name: username }),
        action: handleMentionClick,
        icon: atIcon,
      });

      items.push(null);

      if (status.group) {
        items.push({
          text: isMutingGroup
            ? intl.formatMessage(messages.unmuteGroup)
            : intl.formatMessage(messages.muteGroup),
          icon: volume3Icon,
          action: isMutingGroup ? handleUnmuteGroupClick : handleMuteGroupClick,
        });
        items.push(null);
      }

      items.push({
        text: intl.formatMessage(
          isMutingAccount ? messages.unmute : messages.mute,
          { name: username },
        ),
        action: handleMuteClick,
        icon: isMutingAccount ? volumeIcon : volume3Icon,
      });

      const isBlocking = !!relationship?.blocking;
      items.push({
        text: intl.formatMessage(
          isBlocking ? messages.unblock : messages.block,
          { name: username },
        ),
        action: isBlocking ? handleUnblockClick : handleBlockClick,
        icon: banIcon,
      });
      items.push({
        text: intl.formatMessage(messages.report, { name: username }),
        action: handleReport,
        icon: flagIcon,
      });
    }

    if (isGroupStatus) {
      const group = status.group;
      const account = status.account;
      const isGroupOwner = groupRelationship?.role === GroupRoles.OWNER;
      const isGroupAdmin = groupRelationship?.role === GroupRoles.ADMIN;
      const ownerId = group?.owner?.id;
      const isStatusFromOwner = ownerId === account.id;

      const canBanUser =
        (isGroupOwner || isGroupAdmin) && !isStatusFromOwner && !isOwnAccount;
      const canDeleteStatus =
        !isOwnAccount && (isGroupOwner || (isGroupAdmin && !isStatusFromOwner));

      if (canBanUser || canDeleteStatus) {
        items.push(null);
      }

      if (canBanUser) {
        items.push({
          text: "Ban from Group",
          action: handleBlockFromGroup,
          icon: banIcon,
          destructive: true,
        });
      }

      if (canDeleteStatus) {
        items.push({
          text: intl.formatMessage(messages.groupModDelete),
          action: handleDeleteFromGroup,
          icon: trashIcon,
          destructive: true,
        });
      }
    }

    return items;
  }

  const menu = buildMenuItems();

  return (
    <DropdownMenu
      items={menu}
      status={status}
      width="wide"
      placement={placement}
      onOpen={boostsQueryEnabled ? () => setHasMenuOpened(true) : undefined}
    >
      <StatusActionGroup
        primaryAction={{
          "aria-label": intl.formatMessage(messages.more),
          iconSrc: dotsIcon,
          color: isExpandable ? "default" : "emphasized",
          handler: () => null, // noop
        }}
        layout={layout}
      />
    </DropdownMenu>
  );
}
